import { Link } from 'react-router-dom'
import { SURFACES, BOUNDARY } from '../lib/surfaces.js'
import styles from './PurposeStrip.module.css'

/**
 * THE PURPOSE STRIP — what this screen is, what it is not, and where the other
 * thing lives.
 *
 * Brian: *"my team must understand why each exists and what exists in each.
 * because right now there is too much that is the same."*
 *
 * ⚠️ IT RENDERS THE SHARED CONSTANT, IT DOES NOT CARRY PROSE. Every word comes
 * from `lib/surfaces.js`. The failure being fixed is two screens that each
 * described themselves in their own file and slowly came to describe the same
 * job — so a component holding its own copy of the wording would rebuild the
 * exact bug, one release later and harder to see.
 *
 * ⚠️ AND IT IS ABOVE THE WORK, NOT BELOW IT. A rep who opened the wrong screen
 * needs to find out before they start reading it, not after they scroll.
 *
 * Mobile-first: one stacked column by default, two-up only at 761px. The
 * cross-link is a 44px row in its own right — it is the control that actually
 * fixes a wrong turn, so it is never a hairline of text.
 */
export default function PurposeStrip({ which }) {
  const s = SURFACES[which]
  if (!s) return null

  /* An outside application gets a real new tab and the rel guard; an in-app
   * route gets a Link, so it never reloads the SPA. Two different things that
   * look the same in a design and behave very differently in the hand. */
  const cross = s.otherExternal ? (
    <a
      className={styles.cross}
      href={s.otherHref}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className={styles.crossLabel}>{s.otherWhere}</span>
      <span className={styles.crossGo}>Open {s.otherLabel} ↗</span>
    </a>
  ) : (
    <Link className={styles.cross} to={s.otherHref}>
      <span className={styles.crossLabel}>{s.otherWhere}</span>
      <span className={styles.crossGo}>Open {s.otherLabel} →</span>
    </Link>
  )

  return (
    <section className={`card ${styles.strip}`} aria-label="What this screen is for">
      <div className={styles.badgeRow}>
        <span className={styles.badge} data-audience={s.key}>
          {s.audience}
        </span>
        <span className={styles.rule} aria-hidden="true" />
      </div>

      <div className={styles.grid}>
        <div className={styles.cell}>
          <div className={styles.cellHead}>What this is</div>
          <p className={styles.cellBody}>{s.is}</p>
          {/* BOTH SURFACES SHOW ORDERS, AND NEITHER OWNS THE WORD. Saying
            * "orders live here" on either one would be false on both — they
            * answer different questions from different systems. So each states
            * WHICH orders and for WHOM. See ORDERS_NOTE in lib/surfaces.js. */}
          {s.orders && <p className={styles.cellOrders}>{s.orders}</p>}
        </div>
        <div className={styles.cell} data-tone="not">
          <div className={styles.cellHead}>What this is not</div>
          <p className={styles.cellBody}>{s.isNot}</p>
        </div>
      </div>

      {cross}

      <p className={styles.boundary}>{BOUNDARY}</p>
    </section>
  )
}
